import type { WebMCPTool } from "../core/tool-registry"

const DESTRUCTIVE_NAME_PATTERN = /^(delete|remove|checkout|pay|set_state|clear_cart)/
const DESTRUCTIVE_TAGS = new Set(["crud.delete", "checkout", "payment"])
const HIGH_RISK_RE = /delete|pay|checkout|clear/

export interface ConfirmationResult {
  needsConfirmation: true
  toolName: string
  toolDescription: string
  args: Record<string, unknown>
  callId: string
  /** true for delete/payment, false for set_state */
  isDestructive: boolean
}

/**
 * Programmatic confirmation check. Returns true when the tool
 * should require explicit user approval before execution.
 */
export function requiresConfirmation(
  tool: WebMCPTool,
  _args?: Record<string, unknown>,
): boolean {
  // ReadOnly tools never require confirmation
  if (tool.metadata?.readOnly) return false
  // Check name pattern
  if (DESTRUCTIVE_NAME_PATTERN.test(tool.name)) return true
  // Check tags
  if (tool.metadata?.tags?.some((t) => DESTRUCTIVE_TAGS.has(t))) return true
  return false
}

/**
 * Build a confirmation result payload for a pending tool call.
 */
export function buildConfirmation(
  tool: WebMCPTool,
  args: Record<string, unknown>,
  callId: string,
): ConfirmationResult {
  const isDestructive =
    HIGH_RISK_RE.test(tool.name) ||
    !!tool.metadata?.tags?.some((t) => t === "crud.delete" || t === "payment")

  return {
    needsConfirmation: true,
    toolName: tool.name,
    toolDescription: tool.description,
    args,
    callId,
    isDestructive,
  }
}
